// frontend/src/dashboard/TrialBanner.tsx
//
// Self-contained trial-status banner shown at the top of the dashboard. It
// fetches the tenant subscription, computes day-count math, and renders one of
// three states:
//
//   • status === 'trial'      → countdown + "Set up billing" CTA
//   • status === 'read_only'  → non-dismissable lockout banner + "Reactivate" CTA
//   • status === 'active'     → returns null (no banner)
//
// Day-3+ urgency switches the trial variant to a coral/destructive treatment.
// The trial variant is dismissable per session via `sessionStorage`; the
// read_only variant is intentionally not dismissable.
//
// The "Set up billing" CTA opens `TrialWizardModal` (re-exported from
// ./TrialWizardPage). This file does not import from DashboardShell, HomePage,
// or App — the supervisor wires it in by dropping <TrialBanner /> at the top
// of the dashboard surface.

import * as React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Sparkles, AlertTriangle, X, ArrowRight } from 'lucide-react';
import { cn } from '../lib/utils';
import { billingApi } from '../modules/platform-billing/api';
import type { TenantSubscription } from '../modules/platform-billing/types';
import { TrialWizardModal } from './TrialWizardPage';

const DISMISS_KEY = 'worqly.trialBanner.dismissed';

/** Whole-day count from now to `iso`, floored at 0. */
function daysUntil(iso: string | null | undefined): number {
  if (!iso) return 0;
  const target = new Date(iso).getTime();
  if (Number.isNaN(target)) return 0;
  const now = Date.now();
  const ms = target - now;
  if (ms <= 0) return 0;
  return Math.ceil(ms / (1000 * 60 * 60 * 24));
}

interface TrialBannerProps {
  /** Override the subscription source. When omitted, the banner fetches on mount. */
  subscription?: TenantSubscription | null;
  /** Optional click handler for the primary CTA. Defaults to opening the modal. */
  onCta?: () => void;
  className?: string;
}

export function TrialBanner({ subscription: external, onCta, className }: TrialBannerProps = {}) {
  const [internal, setInternal] = React.useState<TenantSubscription | null | undefined>(
    external !== undefined ? external : undefined,
  );
  const [dismissed, setDismissed] = React.useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    try { return window.sessionStorage.getItem(DISMISS_KEY) === '1'; } catch { return false; }
  });
  const [wizardOpen, setWizardOpen] = React.useState(false);

  // Fetch when no external subscription is supplied. Silently ignore errors —
  // the banner is non-critical chrome and should not break the dashboard.
  React.useEffect(() => {
    if (external !== undefined) { setInternal(external); return; }
    let cancelled = false;
    billingApi.mySubscription()
      .then((sub) => { if (!cancelled) setInternal(sub); })
      .catch(() => { if (!cancelled) setInternal(null); });
    return () => { cancelled = true; };
  }, [external]);

  const sub = internal;
  if (!sub) return null;
  if (sub.status === 'active' || sub.status === 'cancelled' || sub.status === 'suspended' || sub.status === 'past_due') {
    // These states have their own UI on BillingPage; the trial banner stays out of the way.
    return null;
  }

  const isReadOnly = sub.status === 'read_only';
  const isTrial    = sub.status === 'trial';
  if (!isReadOnly && !isTrial) return null;

  // Trial banner can be dismissed once per session. Read-only cannot.
  if (isTrial && dismissed) return null;

  const daysLeft = daysUntil(sub.trial_ends_at);
  const urgent   = isTrial && daysLeft <= 3;

  const tone =
    isReadOnly ? 'lockout' :
    urgent     ? 'urgent'  :
    'trial';

  const styles = TONE_STYLES[tone];

  function openWizard() {
    if (onCta) { onCta(); return; }
    setWizardOpen(true);
  }

  function dismiss() {
    if (isReadOnly) return; // hard rule — lockout is not dismissable
    try { window.sessionStorage.setItem(DISMISS_KEY, '1'); } catch { /* no-op */ }
    setDismissed(true);
  }

  return (
    <>
      <AnimatePresence>
        <motion.div
          key="trial-banner"
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -12 }}
          transition={{ type: 'spring', stiffness: 260, damping: 26 }}
          className={cn('w-full', className)}
          role="status"
          aria-live={isReadOnly ? 'assertive' : 'polite'}
        >
          <div
            className={cn(
              'relative overflow-hidden rounded-3xl shadow-card ring-1',
              styles.surface,
              styles.ring,
            )}
          >
            {/* Left accent stripe */}
            <div className={cn('absolute inset-y-0 left-0 w-1.5', styles.stripe)} />

            <div className="flex flex-col sm:flex-row sm:items-center gap-4 px-5 sm:px-7 py-4 pl-7">
              <div className="flex items-start sm:items-center gap-3 min-w-0 flex-1">
                <div className={cn('shrink-0 h-10 w-10 rounded-full flex items-center justify-center', styles.iconBg)}>
                  {isReadOnly
                    ? <AlertTriangle className={cn('h-5 w-5', styles.iconColor)} strokeWidth={2.2} />
                    : <Sparkles      className={cn('h-5 w-5', styles.iconColor)} strokeWidth={2.2} />}
                </div>
                <div className="min-w-0">
                  <div className={cn('text-[14px] sm:text-[15px] font-semibold leading-tight', styles.title)}>
                    {isReadOnly
                      ? 'Trial ended — add a payment method to continue'
                      : urgent
                        ? daysLeft <= 1
                          ? `Your trial ends ${daysLeft === 0 ? 'today' : 'tomorrow'} — add a card to keep working`
                          : `Only ${daysLeft} days left in your trial`
                        : `Free trial — ${daysLeft} ${daysLeft === 1 ? 'day' : 'days'} left`}
                  </div>
                  <div className={cn('text-[12.5px] sm:text-[13px] mt-0.5 leading-snug', styles.body)}>
                    {isReadOnly
                      ? 'Your workspace is read-only. Reactivate by choosing your modules and adding a card.'
                      : urgent
                        ? 'Pick your modules, set seats, and add a card to switch to paid without interruption.'
                        : 'Pick your modules, set seats, and add a card whenever you\'re ready — no rush.'}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2 sm:gap-3 shrink-0">
                <button
                  type="button"
                  onClick={openWizard}
                  className={cn(
                    'inline-flex items-center gap-2 h-10 px-5 rounded-full text-[13px] font-semibold transition-colors shadow-pill',
                    styles.cta,
                  )}
                >
                  {isReadOnly ? 'Reactivate' : 'Set up billing'}
                  <ArrowRight className="h-4 w-4" strokeWidth={2.2} />
                </button>

                {!isReadOnly && (
                  <button
                    type="button"
                    onClick={dismiss}
                    aria-label="Dismiss"
                    className={cn(
                      'inline-flex items-center justify-center h-9 w-9 rounded-full transition-colors',
                      styles.dismiss,
                    )}
                  >
                    <X className="h-4 w-4" strokeWidth={2.2} />
                  </button>
                )}
              </div>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>

      {/* Wizard modal — local state. Supervisor may also wire the dashboard route
          variant at /dashboard/billing/trial-wizard (see TrialWizardPage). */}
      <TrialWizardModal
        open={wizardOpen}
        onOpenChange={setWizardOpen}
        subscription={sub}
      />
    </>
  );
}

/* ────────────────────────────────────────────────────────────────────
 * Style tones
 * ────────────────────────────────────────────────────────────────────
 * Three visual treatments share the same banner shape. All work in light
 * + dark mode via dark: variants. We avoid raw colors outside the
 * coral/ink/lavender palette so the banner sits comfortably on the
 * pearl canvas.
 */
type ToneKey = 'trial' | 'urgent' | 'lockout';
type Tone = {
  surface:    string;
  ring:       string;
  stripe:     string;
  iconBg:     string;
  iconColor:  string;
  title:      string;
  body:       string;
  cta:        string;
  dismiss:    string;
};

const TONE_STYLES: Record<ToneKey, Tone> = {
  trial: {
    surface:   'bg-lavender-50/80 dark:bg-white/[0.04] backdrop-blur-xl',
    ring:      'ring-lavender-200/60 dark:ring-white/10',
    stripe:    'bg-lavender-400',
    iconBg:    'bg-white/80 dark:bg-white/10 ring-1 ring-lavender-200/70 dark:ring-white/10',
    iconColor: 'text-lavender-700 dark:text-lavender-300',
    title:     'text-ink dark:text-white',
    body:      'text-ink/65 dark:text-white/65',
    cta:       'bg-ink text-white hover:bg-ink-800 dark:bg-white dark:text-ink dark:hover:bg-white/90',
    dismiss:   'text-ink/45 hover:text-ink hover:bg-white/60 dark:text-white/50 dark:hover:text-white dark:hover:bg-white/10',
  },
  urgent: {
    surface:   'bg-coral-100/90 dark:bg-coral/15 backdrop-blur-xl',
    ring:      'ring-coral-200/80 dark:ring-coral/30',
    stripe:    'bg-coral',
    iconBg:    'bg-white/90 dark:bg-white/10 ring-1 ring-coral-200/80 dark:ring-coral/30',
    iconColor: 'text-coral-700 dark:text-coral-200',
    title:     'text-coral-700 dark:text-coral-100',
    body:      'text-ink/70 dark:text-white/70',
    cta:       'bg-coral text-white shadow-cta hover:bg-coral-600',
    dismiss:   'text-ink/45 hover:text-ink hover:bg-white/60 dark:text-white/55 dark:hover:text-white dark:hover:bg-white/10',
  },
  lockout: {
    surface:   'bg-rose-500/10 dark:bg-rose-500/15 backdrop-blur-xl',
    ring:      'ring-rose-500/30',
    stripe:    'bg-rose-500',
    iconBg:    'bg-white/90 dark:bg-white/10 ring-1 ring-rose-500/30',
    iconColor: 'text-rose-600 dark:text-rose-300',
    title:     'text-rose-700 dark:text-rose-200',
    body:      'text-ink/70 dark:text-white/70',
    cta:       'bg-rose-600 text-white hover:bg-rose-700 shadow-cta',
    dismiss:   '', // unused for lockout
  },
};

/* ────────────────────────────────────────────────────────────────────
 * Convenience: top-of-dashboard wrapper.
 * ────────────────────────────────────────────────────────────────────
 * Sized to sit in the dashboard's content area at the very top, full
 * width with the standard horizontal padding the rest of the dashboard
 * uses. The supervisor can drop <DashboardTrialBannerSlot /> directly
 * into the shell.
 */
export function DashboardTrialBannerSlot() {
  return (
    <div className="px-4 sm:px-6 lg:px-10 pt-4">
      <TrialBanner />
      <TrialBannerHelperLink />
    </div>
  );
}

/** Optional secondary affordance — links to the standalone wizard route for
 *  no-JS environments. Rendered inside <noscript>. */
function TrialBannerHelperLink() {
  return (
    <noscript>
      <Link to="/dashboard/billing/trial-wizard" className="sr-only">
        Open the trial wizard
      </Link>
    </noscript>
  );
}

export default TrialBanner;
